/*
 * SawaKart theme: <sk-carousel> and <sk-hero> (package P2).
 *
 * <sk-carousel> re-implements the parts of embla-carousel 8.6.0 that the FE
 * ProductCarousel uses ({align:'start', slidesToScroll:1, containScroll:'trimSnaps',
 * loop:false, dragThreshold:10, duration:25}) with embla's own constants, so the
 * snapping, drag, flick and edge rubber-band feel the same. No library.
 *
 * <sk-hero> is mtstorelb.com's hero (9 Oct 2026): full-width slides that slide
 * sideways in an endless loop, copy that enters as its slide arrives, progress
 * bars that time the autoplay, a Pause button, drag/swipe and arrow keys.
 *
 * Both elements set themselves up in connectedCallback and clean up in
 * disconnectedCallback, so theme-editor section reloads need no extra code.
 */
(function () {
  'use strict';

  function clamp(n, min, max) {
    return Math.min(Math.max(n, min), max);
  }
  function isMouseEvent(evt) {
    return typeof window.MouseEvent !== 'undefined' && evt instanceof window.MouseEvent;
  }
  /* embla factorAbs */
  function factorAbs(valueB, valueA) {
    if (valueB === 0 || valueA === 0) return 0;
    if (Math.abs(valueB) <= Math.abs(valueA)) return 0;
    return Math.abs((Math.abs(valueB) - Math.abs(valueA)) / valueB);
  }

  var FIXED_STEP = 1000 / 60;
  var BASE_DURATION = 25;
  var BASE_FRICTION = 0.68;
  var DRAG_THRESHOLD = 10;
  var LOG_INTERVAL = 170;
  var PIXEL_TOLERANCE = 2;
  var FOCUS_NODES = ['INPUT', 'SELECT', 'TEXTAREA'];
  var NON_PASSIVE = { passive: false };
  var PASSIVE = { passive: true };

  /* ------------------------------------------------------------------ */
  /* <sk-carousel>                                                       */
  /* ------------------------------------------------------------------ */

  class SkCarousel extends HTMLElement {
    constructor() {
      super();
      this._tick = this._tick.bind(this);
      this._onDown = this._onDown.bind(this);
      this._onMove = this._onMove.bind(this);
      this._onUp = this._onUp.bind(this);
      this._onClick = this._onClick.bind(this);
      this._onDragStart = function (evt) { evt.preventDefault(); };
      this._noop = function () {};
      this._onKeyDown = this._onKeyDown.bind(this);
      this._onVisibility = this._onVisibility.bind(this);
      this._onSlideFocus = this._onSlideFocus.bind(this);
      this._onPrev = this.scrollPrev.bind(this);
      this._onNext = this.scrollNext.bind(this);
    }

    connectedCallback() {
      if (this._connected) return;
      this.viewport = this.querySelector('.sk-pcar__viewport');
      this.container = this.viewport && this.viewport.querySelector('.sk-pcar__container');
      if (!this.container) return;
      this._connected = true;
      this.prevBtn = this.querySelector('.sk-pcar__arrow--prev');
      this.nextBtn = this.querySelector('.sk-pcar__arrow--next');
      this.thumb = this.querySelector('.sk-pcar__thumb');
      this.slides = Array.prototype.slice.call(this.container.children);

      this.raf = 0;
      this.lastTs = null;
      this.acc = 0;
      this.pointerIsDown = false;
      this.preventScroll = false;
      this.preventClick = false;
      this.isMouse = false;
      this.dragNode = null;
      this.lastTabPress = 0;
      this.index = 0;

      var vp = this.viewport;
      vp.addEventListener('dragstart', this._onDragStart, NON_PASSIVE);
      vp.addEventListener('touchmove', this._noop, NON_PASSIVE);
      vp.addEventListener('touchend', this._noop, PASSIVE);
      vp.addEventListener('touchstart', this._onDown, PASSIVE);
      vp.addEventListener('mousedown', this._onDown, PASSIVE);
      vp.addEventListener('touchcancel', this._onUp, PASSIVE);
      vp.addEventListener('contextmenu', this._onUp, PASSIVE);
      vp.addEventListener('click', this._onClick, true);
      var self = this;
      this.slides.forEach(function (slide) {
        slide.addEventListener('focus', self._onSlideFocus, { passive: true, capture: true });
      });
      document.addEventListener('keydown', this._onKeyDown, false);
      document.addEventListener('visibilitychange', this._onVisibility);
      if (this.prevBtn) this.prevBtn.addEventListener('click', this._onPrev);
      if (this.nextBtn) this.nextBtn.addEventListener('click', this._onNext);

      this._activate(0);

      if (typeof ResizeObserver !== 'undefined') {
        this.ro = new ResizeObserver(function (entries) {
          if (!self._connected) return;
          for (var i = 0; i < entries.length; i++) {
            var node = entries[i].target;
            var last = self._sizes.get(node);
            if (last === undefined) continue;
            if (Math.abs(node.offsetWidth - last) >= 0.5) {
              self.reInit();
              break;
            }
          }
        });
        var nodes = [this.container].concat(this.slides);
        this._roFrame = requestAnimationFrame(function () {
          self._roFrame = 0;
          if (!self._connected) return;
          nodes.forEach(function (n) { self.ro.observe(n); });
        });
      }
    }

    disconnectedCallback() {
      if (!this._connected) return;
      this._connected = false;
      this._stop();
      this._clearDragEvents();
      if (this._roFrame) cancelAnimationFrame(this._roFrame);
      if (this.ro) this.ro.disconnect();
      this.ro = null;
      var vp = this.viewport;
      vp.removeEventListener('dragstart', this._onDragStart, NON_PASSIVE);
      vp.removeEventListener('touchmove', this._noop, NON_PASSIVE);
      vp.removeEventListener('touchend', this._noop, PASSIVE);
      vp.removeEventListener('touchstart', this._onDown, PASSIVE);
      vp.removeEventListener('mousedown', this._onDown, PASSIVE);
      vp.removeEventListener('touchcancel', this._onUp, PASSIVE);
      vp.removeEventListener('contextmenu', this._onUp, PASSIVE);
      vp.removeEventListener('click', this._onClick, true);
      var self = this;
      this.slides.forEach(function (slide) {
        slide.removeEventListener('focus', self._onSlideFocus, { passive: true, capture: true });
      });
      document.removeEventListener('keydown', this._onKeyDown, false);
      document.removeEventListener('visibilitychange', this._onVisibility);
      if (this.prevBtn) this.prevBtn.removeEventListener('click', this._onPrev);
      if (this.nextBtn) this.nextBtn.removeEventListener('click', this._onNext);
    }

    /* ---------- measurement (embla SlideSizes / ScrollSnaps / ScrollContain / ScrollLimit) ---------- */

    _measure() {
      var slides = this.slides;
      var n = slides.length;
      var viewSize = this.container.offsetWidth;
      this.viewSize = viewSize;
      this._sizes = new Map();
      this._sizes.set(this.container, viewSize);
      slides.forEach(function (s) { this._sizes.set(s, s.offsetWidth); }, this);

      if (!n) {
        this.snaps = [0];
        this.limitMin = 0;
        this.limitMax = 0;
        this.registry = [];
        return;
      }
      /* The container is position:relative, so offsetLeft is container-relative
         (embla: containerRect.left − slideRect.left with a shared offsetParent). */
      var lefts = slides.map(function (s) { return s.offsetLeft; });
      var sizes = slides.map(function (s) { return s.offsetWidth; });
      var snaps = lefts.map(function (l) { return -Math.abs(l); });
      var startGap = Math.abs(lefts[0]);
      var endGap = parseFloat(window.getComputedStyle(slides[n - 1]).getPropertyValue('margin-right')) || 0;
      var lastWithGap;
      if (n === 1) lastWithGap = sizes[0] + startGap;
      else lastWithGap = sizes[n - 1] + endGap;
      var contentSize = -snaps[n - 1] + lastWithGap;

      /* ScrollContain (trimSnaps) */
      var bMin = -contentSize + viewSize;
      var bMax = 0;
      var bounded = snaps.map(function (snapAligned, i) {
        var snap = snapAligned < bMin ? bMin : snapAligned > bMax ? bMax : snapAligned;
        if (i === 0) return bMax;
        if (i === n - 1) return bMin;
        if (Math.abs(bMin - snap) <= 1) return bMin;
        if (Math.abs(bMax - snap) <= 1) return bMax;
        return snap;
      }).map(function (v) { return parseFloat(v.toFixed(3)); });
      var cMin = bounded.lastIndexOf(bounded[0]);
      var cMax = bounded.indexOf(bounded[bounded.length - 1]) + 1;
      var contained;
      if (contentSize <= viewSize + PIXEL_TOLERANCE) contained = [bMax];
      else contained = bounded.slice(cMin, cMax);

      this.snaps = contained;
      this.limitMax = contained[0];
      this.limitMin = contained[contained.length - 1];

      /* SlideRegistry: slide index -> snap index (used by watchFocus) */
      var registry = [];
      var lastGroup = contained.length - 1;
      for (var i = 0; i < n; i++) {
        if (contained.length === 1 || i <= cMin) registry.push(0);
        else if (i >= cMax - 1) registry.push(lastGroup);
        else registry.push(i - cMin);
      }
      this.registry = registry;
    }

    _activate(startIndex) {
      this._measure();
      var last = this.snaps.length - 1;
      this.index = clamp(startIndex || 0, 0, last);
      var start = this.snaps[this.index];
      this.location = start;
      this.previousLocation = start;
      this.offsetLocation = start;
      this.target = start;
      this.velocity = 0;
      this.duration = BASE_DURATION;
      this.friction = BASE_FRICTION;
      this._translated = null;
      this.goToNextThreshold = clamp(this.viewSize * 0.2, 50, 225);
      this._translate(this.location);
      this._syncUi();
    }

    reInit() {
      if (!this._connected) return;
      var start = this.index;
      this._stop();
      this._clearDragEvents();
      this.pointerIsDown = false;
      this.preventScroll = false;
      this.isMouse = false;
      this.container.style.transform = '';
      this._activate(start);
      if (this.ro) {
        var self = this;
        this.ro.disconnect();
        [this.container].concat(this.slides).forEach(function (n) { self.ro.observe(n); });
      }
    }

    /* ---------- UI (FE onSelect / onScroll handlers) ---------- */

    _syncUi() {
      this._updateButtons();
      this._updateProgress();
    }

    _updateButtons() {
      var canPrev = this.canScrollPrev();
      var canNext = this.canScrollNext();
      if (this.prevBtn) this.prevBtn.disabled = !canPrev;
      if (this.nextBtn) this.nextBtn.disabled = !canNext;
      if (canPrev || canNext) this.setAttribute('data-scrollable', '');
      else this.removeAttribute('data-scrollable');
    }

    _updateProgress() {
      if (!this.thumb) return;
      var length = Math.abs(this.limitMin - this.limitMax);
      var p = length ? (this.offsetLocation - this.limitMax) / -length : 0;
      p = clamp(p, 0, 1);
      this.thumb.style.transform = 'translateX(' + p * 300 + '%)';
    }

    canScrollPrev() {
      return clamp(this.index - 1, 0, this.snaps.length - 1) !== this.index;
    }

    canScrollNext() {
      return clamp(this.index + 1, 0, this.snaps.length - 1) !== this.index;
    }

    /* ---------- limits ---------- */

    _reachedMin(n) { return n < this.limitMin; }
    _reachedMax(n) { return n > this.limitMax; }
    _reachedAny(n) { return n < this.limitMin || n > this.limitMax; }
    _constrain(n) {
      if (!this._reachedAny(n)) return n;
      return n < this.limitMin ? this.limitMin : this.limitMax;
    }

    /* ---------- ScrollBounds ---------- */

    _shouldConstrain() {
      return this._reachedAny(this.target) && this._reachedAny(this.offsetLocation);
    }

    _constrainBounds(pointerDown) {
      if (!this._shouldConstrain()) return;
      var loc = this.offsetLocation;
      var edge = this._reachedMin(loc) ? this.limitMin : this.limitMax;
      var diffToEdge = Math.abs(edge - loc);
      var diffToTarget = this.target - loc;
      var friction = clamp(diffToEdge / (this.viewSize * 0.5), 0.1, 0.99);
      this.target -= diffToTarget * friction;
      if (!pointerDown && Math.abs(diffToTarget) < this.viewSize * 0.1) {
        this.target = this._constrain(this.target);
        this.duration = 25;
        this.friction = BASE_FRICTION;
      }
    }

    /* ---------- ScrollBody ---------- */

    _seek() {
      var displacement = this.target - this.location;
      if (!this.duration) {
        this.velocity = 0;
        this.previousLocation = this.target;
        this.location = this.target;
      } else {
        this.previousLocation = this.location;
        this.velocity += displacement / this.duration;
        this.velocity *= this.friction;
        this.location += this.velocity;
      }
    }

    _settled() {
      return Math.abs(this.target - this.offsetLocation) < 0.001;
    }

    /* ---------- engine update / render ---------- */

    _update() {
      this._constrainBounds(this.pointerIsDown);
      this._seek();
    }

    _render(alpha) {
      var hasSettled = this._settled() && !this._shouldConstrain();
      var idle = hasSettled && !this.pointerIsDown;
      if (idle) this._stop();
      this.offsetLocation = this.location * alpha + this.previousLocation * (1 - alpha);
      this._translate(this.offsetLocation);
      if (!hasSettled) this._updateProgress();
    }

    _translate(n) {
      var value = Math.round(n * 100) / 100;
      if (value === this._translated) return;
      this.container.style.transform = 'translate3d(' + value + 'px,0px,0px)';
      this._translated = value;
    }

    /* ---------- Animations (fixed 60 Hz step + interpolation) ---------- */

    _start() {
      if (this.raf) return;
      this.raf = requestAnimationFrame(this._tick);
    }

    _stop() {
      if (this.raf) cancelAnimationFrame(this.raf);
      this.lastTs = null;
      this.acc = 0;
      this.raf = 0;
    }

    _tick(ts) {
      if (!this.raf) return;
      if (!this.lastTs) {
        this.lastTs = ts;
        this._update();
        this._update();
      }
      var elapsed = ts - this.lastTs;
      this.lastTs = ts;
      this.acc += elapsed;
      while (this.acc >= FIXED_STEP) {
        this._update();
        this.acc -= FIXED_STEP;
      }
      this._render(this.acc / FIXED_STEP);
      if (this.raf) this.raf = requestAnimationFrame(this._tick);
    }

    _onVisibility() {
      if (document.hidden) {
        this.lastTs = null;
        this.acc = 0;
      }
    }

    /* ---------- ScrollTarget / ScrollTo ---------- */

    _byDistance(distance, snap) {
      var t = this.target + distance;
      var d = this._constrain(t);
      var best = 0;
      var bestDiff = Infinity;
      for (var i = 0; i < this.snaps.length; i++) {
        var diff = Math.abs(this.snaps[i] - d);
        if (diff < bestDiff) {
          bestDiff = diff;
          best = i;
        }
      }
      if (!snap || this._reachedAny(t)) return { index: best, distance: distance };
      return { index: best, distance: distance + (this.snaps[best] - d) };
    }

    _byIndex(index) {
      return { index: index, distance: this.snaps[index] - this.target };
    }

    _scrollTo(t) {
      this.target += t.distance;
      if (t.distance) {
        if (this.duration) {
          this._start();
        } else {
          this._update();
          this._render(1);
          this._update();
        }
      }
      if (t.index !== this.index) {
        this.index = t.index;
        this._updateButtons();
      }
    }

    scrollTo(index, jump) {
      if (!this._connected) return;
      this.friction = BASE_FRICTION;
      this.duration = jump === true ? 0 : BASE_DURATION;
      this._scrollTo(this._byIndex(clamp(index, 0, this.snaps.length - 1)));
    }

    scrollNext() {
      this.scrollTo(this.index + 1);
    }

    scrollPrev() {
      this.scrollTo(this.index - 1);
    }

    /* ---------- DragHandler + DragTracker ---------- */

    _point(evt, axis) {
      var src = isMouseEvent(evt) ? evt : evt.touches && evt.touches[0];
      if (!src) return 0;
      return axis === 'y' ? src.clientY : src.clientX;
    }

    _snapshot(evt) {
      return { x: this._point(evt), t: evt.timeStamp };
    }

    _addDragEvents() {
      var node = this.isMouse ? document : this.viewport;
      this.dragNode = node;
      node.addEventListener('touchmove', this._onMove, NON_PASSIVE);
      node.addEventListener('touchend', this._onUp, PASSIVE);
      node.addEventListener('mousemove', this._onMove, NON_PASSIVE);
      node.addEventListener('mouseup', this._onUp, PASSIVE);
    }

    _clearDragEvents() {
      var node = this.dragNode;
      if (!node) return;
      node.removeEventListener('touchmove', this._onMove, NON_PASSIVE);
      node.removeEventListener('touchend', this._onUp, PASSIVE);
      node.removeEventListener('mousemove', this._onMove, NON_PASSIVE);
      node.removeEventListener('mouseup', this._onUp, PASSIVE);
      this.dragNode = null;
    }

    _onDown(evt) {
      var isMouseEvt = isMouseEvent(evt);
      this.isMouse = isMouseEvt;
      this.preventClick = false;
      if (isMouseEvt && evt.button !== 0) return;
      if (FOCUS_NODES.indexOf(evt.target && evt.target.nodeName) > -1) return;
      this._clearDragEvents();
      this.pointerIsDown = true;
      this.trackStart = this._snapshot(evt);
      this.trackLast = this.trackStart;
      this.friction = 0;
      this.duration = 0;
      this.target = this.location;
      this._addDragEvents();
      this.startScroll = this._point(evt);
      this.startCross = this._point(evt, 'y');
    }

    _onMove(evt) {
      var isTouchEvt = !isMouseEvent(evt);
      if (isTouchEvt && evt.touches.length >= 2) return this._onUp(evt);
      var lastScroll = this._point(evt);
      var lastCross = this._point(evt, 'y');
      var diffScroll = Math.abs(lastScroll - this.startScroll);
      var diffCross = Math.abs(lastCross - this.startCross);
      if (!this.preventScroll && !this.isMouse) {
        if (!evt.cancelable) return this._onUp(evt);
        this.preventScroll = diffScroll > diffCross;
        if (!this.preventScroll) return this._onUp(evt);
      }
      /* DragTracker.pointerMove */
      var snap = this._snapshot(evt);
      var diff = snap.x - this.trackLast.x;
      var expired = snap.t - this.trackStart.t > LOG_INTERVAL;
      this.trackLast = snap;
      if (expired) this.trackStart = snap;

      if (diffScroll > DRAG_THRESHOLD) this.preventClick = true;
      this.friction = 0.3;
      this.duration = 0.75;
      this._start();
      this.target += diff;
      evt.preventDefault();
    }

    _trackerUp(evt) {
      var start = this.trackStart;
      var last = this.trackLast;
      if (!start || !last) return 0;
      var diffDrag = last.x - start.x;
      var diffTime = evt.timeStamp - start.t;
      var expired = evt.timeStamp - last.t > LOG_INTERVAL;
      var force = diffDrag / diffTime;
      var isFlick = diffTime && !expired && Math.abs(force) > 0.1;
      return isFlick ? force : 0;
    }

    _allowedForce(force) {
      var next = clamp(this.index + Math.sign(force) * -1, 0, this.snaps.length - 1);
      var baseForce = this._byDistance(force, true).distance;
      if (Math.abs(force) < this.goToNextThreshold) return baseForce;
      return this._byIndex(next).distance;
    }

    _onUp(evt) {
      var rawForce = this._trackerUp(evt) * (this.isMouse ? 300 : 400);
      var force = this._allowedForce(rawForce);
      var forceFactor = factorAbs(rawForce, force);
      var speed = BASE_DURATION - 10 * forceFactor;
      var friction = BASE_FRICTION + forceFactor / 50;
      this.preventScroll = false;
      this.pointerIsDown = false;
      this._clearDragEvents();
      this.duration = speed;
      this.friction = friction;
      this._scrollTo(this._byDistance(force, true));
      this.isMouse = false;
    }

    _onClick(evt) {
      if (this.preventClick) {
        evt.stopPropagation();
        evt.preventDefault();
        this.preventClick = false;
      }
    }

    /* ---------- SlideFocus (watchFocus) ---------- */

    _onKeyDown(evt) {
      if (evt.code === 'Tab') this.lastTabPress = new Date().getTime();
    }

    _onSlideFocus(evt) {
      if (new Date().getTime() - this.lastTabPress > 10) return;
      var i = this.slides.indexOf(evt.currentTarget);
      if (i < 0) return;
      this.viewport.scrollLeft = 0;
      var group = this.registry[i];
      if (typeof group !== 'number') return;
      this.duration = 0;
      this._scrollTo(this._byIndex(group));
    }
  }

  if (!customElements.get('sk-carousel')) customElements.define('sk-carousel', SkCarousel);

  /* ------------------------------------------------------------------ */
  /* <sk-hero>                                                           */
  /* ------------------------------------------------------------------ */
  /*
   * mtstorelb.com's hero (9 Oct 2026). The slides sit side by side in a track and changing slide
   * slides the track (.75s on cubic-bezier(.16,1,.3,1), the curve measured on their site). The
   * loop is endless: at rest the current slide is second in the track, and slides that are out of
   * view move from one end of the track to the other (the picture on screen never moves), so
   * there is always a slide on both sides. The active progress bar fills over the interval and
   * its animationend moves on, so pausing the bar (Pause button, a drag) pauses the slideshow.
   * A slide's copy enters as it arrives. Drag/swipe and the arrow keys move too; no autoplay
   * for reduced motion; in the theme editor a selected slide stays.
   */
  var HERO_DRAG_START = 8;      /* px before a press becomes a horizontal drag */
  var HERO_SWIPE_SHARE = 0.12;  /* share of the width that changes the slide on release */
  var HERO_FLICK_SPEED = 0.4;   /* px/ms that changes the slide whatever the distance */
  var HERO_EDITOR_EVENTS = ['shopify:block:select', 'shopify:block:deselect', 'shopify:section:select', 'shopify:section:deselect'];

  function heroReducedMotion() {
    return !!((window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) ||
      (document.body && document.body.hasAttribute('data-reduce-animations')));
  }

  class SkHero extends HTMLElement {
    constructor() {
      super();
      this._onClick = this._onClick.bind(this);
      this._onKey = this._onKey.bind(this);
      this._onAnimEnd = this._onAnimEnd.bind(this);
      this._onTransEnd = this._onTransEnd.bind(this);
      this._onDown = this._onDown.bind(this);
      this._onMove = this._onMove.bind(this);
      this._onUp = this._onUp.bind(this);
      this._onEditor = this._onEditor.bind(this);
    }

    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      var self = this;
      this.track = this.querySelector('.sk-hero__track');
      /* in slide order (data-index), whatever order the loop has left them in the track */
      this.slides = Array.prototype.slice.call(this.querySelectorAll('.sk-hero__slide')).sort(function (a, b) {
        return (parseInt(a.getAttribute('data-index'), 10) || 0) - (parseInt(b.getAttribute('data-index'), 10) || 0);
      });
      this.count = this.slides.length;
      if (!this.track || !this.count) return;
      this.bars = Array.prototype.slice.call(this.querySelectorAll('.sk-hero__bar'));
      this.pauseBtn = this.querySelector('.sk-hero__pause');
      var ms = parseInt(this.getAttribute('data-autoplay'), 10);
      this.ms = ms > 0 ? ms : 6000;
      this.style.setProperty('--sk-hero-ms', this.ms + 'ms');
      this.reduced = heroReducedMotion();
      this.paused = false;
      this.editorHold = false;
      this.moving = false;
      this._drag = null;
      var active = this.querySelector('.sk-hero__slide.is-active');
      this.current = Math.max(0, this.slides.indexOf(active));
      this.pos = this._posOf(this.slides[this.current]);

      if (this.count > 1) {
        this._normalize();
        this.addEventListener('click', this._onClick, true);
        this.addEventListener('keydown', this._onKey);
        this.addEventListener('animationend', this._onAnimEnd);
        this.track.addEventListener('transitionend', this._onTransEnd);
        this.addEventListener('pointerdown', this._onDown);
        HERO_EDITOR_EVENTS.forEach(function (name) { document.addEventListener(name, self._onEditor); });
      }
      this._setActive(this.current);
      this._showCopy(this.slides[this.current]);

      /* the first picture settles once (CSS animation); then nothing re-runs it */
      clearTimeout(this._settleTimer);
      this._settleTimer = setTimeout(function () { self.classList.add('is-settled'); }, 2400);
      /* SK-63: only the neighbours are fetched ahead (next and previous), 1.5 s after the first picture or at
         the first touch; every slide change fetches the new neighbours. No slide arrives empty, and a phone no
         longer downloads all the pictures up front. */
      clearTimeout(this._loadTimer);
      this._loadTimer = setTimeout(function () {
        self._warmReady = true;
        self._warmAround(self.current);
      }, 1500);
      if (!this._warmBound) {
        this._warmBound = true;
        this.addEventListener('pointerdown', function () {
          self._warmReady = true;
          self._warmAround(self.current);
        }, { passive: true });
      }
      this._run();
    }

    disconnectedCallback() {
      if (!this._connected) return;
      this._connected = false;
      var self = this;
      clearTimeout(this._settleTimer);
      clearTimeout(this._loadTimer);
      clearTimeout(this._swallowTimer);
      clearTimeout(this._waitTimer);
      this._endDrag();
      this.removeEventListener('click', this._onClick, true);
      this.removeEventListener('keydown', this._onKey);
      this.removeEventListener('animationend', this._onAnimEnd);
      if (this.track) this.track.removeEventListener('transitionend', this._onTransEnd);
      this.removeEventListener('pointerdown', this._onDown);
      HERO_EDITOR_EVENTS.forEach(function (name) { document.removeEventListener(name, self._onEditor); });
    }

    /* ---------- track position ---------- */

    _posOf(el) {
      return Array.prototype.indexOf.call(this.track.children, el);
    }

    /* move the track to position p (fractional while dragging), animated or at once */
    _set(p, animate) {
      this.pos = p;
      this.track.classList.toggle('is-moving', !!animate && !this.reduced);
      this.track.style.transform = 'translate3d(' + (-100 * p) + '%, 0, 0)';
    }

    /* where the track is right now, in slides (fractional while a move runs) */
    _livePos() {
      var m = window.getComputedStyle(this.track).transform;
      var w = this.track.getBoundingClientRect().width || 1;
      if (!m || m === 'none') return 0;
      var v = m.slice(m.indexOf('(') + 1, -1).split(',');
      var x = parseFloat(v.length === 16 ? v[12] : v[4]) || 0;
      return -x / w;
    }

    /* stop a running move where it is (the picture stays still) */
    _freeze() {
      if (!this.moving) return;
      this.moving = false;
      this._set(this._livePos(), false);
      void this.track.offsetWidth;
    }

    /* at rest the current slide is second in the track, so both neighbours are ready;
       the slides moved are out of view, and the transform follows, so nothing moves on screen */
    _normalize() {
      var t = this.track;
      var guard = this.count;
      var p = this._posOf(this.slides[this.current]);
      while (p > 1 && guard-- > 0) { t.appendChild(t.firstElementChild); p--; }
      while (p < 1 && guard-- > 0) { t.insertBefore(t.lastElementChild, t.firstElementChild); p++; }
      this._set(p, false);
    }

    /* ---------- moving ---------- */

    next() { this.goTo(this.current + 1, 1); }
    prev() { this.goTo(this.current - 1, -1); }

    /* go to slide `index`: dir 1 forward, -1 backward, 0 the shorter way round */
    goTo(index, dir) {
      var n = this.count;
      if (n < 2 || !this._connected) return;
      var t = this.track;
      var target = ((index % n) + n) % n;
      this._freeze();
      var fwd = (target - this.current + n) % n;
      var back = (this.current - target + n) % n;
      if (!dir) dir = fwd <= back ? 1 : -1;
      var curPos = this._posOf(this.slides[this.current]);
      var dest = curPos + (dir > 0 ? fwd : -back);
      /* make room on the side the track is heading to, with slides that are out of view */
      var guard = n;
      while (dest > n - 1 && this.pos >= 1 && guard-- > 0) {
        t.appendChild(t.firstElementChild);
        dest--;
        this._set(this.pos - 1, false);
      }
      while (dest < 0 && this.pos <= n - 2 && guard-- > 0) {
        t.insertBefore(t.lastElementChild, t.firstElementChild);
        dest++;
        this._set(this.pos + 1, false);
      }
      /* no room that way (a long move asked for in the middle of another): go straight to the
         slide where it is, so a move never lands on another picture and then jumps */
      if (dest < 0 || dest > n - 1) dest = this._posOf(this.slides[target]);
      this._arrive(target);
      this._moveTo(dest);
    }

    /* slide `target` becomes the current one: its bar runs, its copy enters (unless still on show) */
    _arrive(target) {
      var slide = this.slides[target];
      this.current = target;
      this._setActive(target);
      var copy = slide.querySelector('.sk-hero__copy');
      if (copy && !copy.classList.contains('is-shown')) this._showCopy(slide);
      this._run();
    }

    /* slide the track to position `dest` (at once for reduced motion) */
    _moveTo(dest) {
      if (this.reduced || Math.abs(this.pos - dest) < 0.001) {
        this._set(dest, false);
        this._rest();
        return;
      }
      void this.track.offsetWidth; /* commit the start position so the move animates from it */
      this.moving = true;
      this._set(dest, true);
    }

    _onTransEnd(evt) {
      if (evt.target !== this.track || evt.propertyName !== 'transform' || !this.moving) return;
      this._rest();
    }

    /* after a move: current slide back to second place; slides out of view replay their copy next time */
    _rest() {
      this.moving = false;
      this._normalize();
      var cur = this.slides[this.current];
      this.slides.forEach(function (s) {
        if (s === cur) return;
        var c = s.querySelector('.sk-hero__copy');
        if (c) c.classList.remove('is-shown');
      });
    }

    _setActive(index) {
      this.slides.forEach(function (s, i) {
        var on = i === index;
        s.classList.toggle('is-active', on);
        if (on) s.classList.remove('is-cold'); /* SK-71: a shown slide always has its picture */
        s.setAttribute('aria-hidden', on ? 'false' : 'true');
        if (on) s.removeAttribute('inert');
        else s.setAttribute('inert', '');
      });
      if (this._warmReady) this._warmAround(index);
    }

    _warmAround(index) {
      var n = this.slides.length;
      if (!n) return;
      [index, index + 1, index - 1].forEach(function (k) { /* the slide itself too: a bar jump goes straight to it */
        var s = this.slides[((k % n) + n) % n];
        if (s) s.classList.remove('is-cold'); /* SK-71: its picture exists (and loads) from now on */
        var img = s && s.querySelector('.sk-hero__img[loading="lazy"]');
        if (img) img.loading = 'eager';
      }, this);
    }

    _showCopy(slide) {
      var copy = slide && slide.querySelector('.sk-hero__copy');
      if (!copy) return;
      copy.classList.remove('is-shown');
      void copy.offsetWidth; /* commit the hidden state so the entrance runs */
      copy.classList.add('is-shown');
    }

    /* ---------- progress bars (the autoplay timer) ---------- */

    _run() {
      var self = this;
      this.bars.forEach(function (b, i) {
        b.setAttribute('aria-current', i === self.current ? 'true' : 'false');
        var f = b.firstElementChild;
        if (f) f.removeAttribute('data-run');
      });
      if (this.count < 2 || this.reduced || this.paused || this.editorHold || !this._connected) return;
      var fill = this.bars[this.current] && this.bars[this.current].firstElementChild;
      if (!fill) return;
      void fill.offsetWidth; /* restart the fill animation */
      fill.setAttribute('data-run', 'true');
    }

    _onAnimEnd(evt) {
      if (evt.animationName !== 'sk-hero-fill' || !evt.target.getAttribute || evt.target.getAttribute('data-run') !== 'true') return;
      this._nextWhenReady(0);
    }

    /* SK-71: autoplay moves on only once the next slide's photo is there (it is fetched ahead, but on a slow
       connection it can still be on its way); it checks every 0.3 s and goes anyway after 8 s. A tap on a bar,
       an arrow key or a swipe still moves at once. */
    _nextWhenReady(waited) {
      var self = this;
      var n = this.slides.length;
      if (!n || !this._connected) return;
      if (this.paused || this.editorHold) { this._pendingNext = true; return; }
      this._pendingNext = false;
      var k = (this.current + 1) % n;
      var img = this.slides[k] && this.slides[k].querySelector('.sk-hero__img');
      if (!img || (img.complete && img.naturalWidth > 0) || waited >= 8000) {
        this.next();
        return;
      }
      if (!this._warmReady) {
        this._warmReady = true;
        this._warmAround(this.current);
      }
      clearTimeout(this._waitTimer);
      this._waitTimer = setTimeout(function () { self._nextWhenReady(waited + 300); }, 300);
    }

    _togglePause() {
      this.paused = !this.paused;
      if (this.paused) this.setAttribute('data-paused', '');
      else this.removeAttribute('data-paused');
      if (this.pauseBtn) {
        this.pauseBtn.textContent = this.paused ? 'Play' : 'Pause';
        this.pauseBtn.setAttribute('aria-pressed', this.paused ? 'true' : 'false');
      }
      if (!this.paused && this._pendingNext) {
        /* SK-71: paused while waiting for the next photo: carry on from there */
        this._nextWhenReady(0);
        return;
      }
      if (!this.paused) {
        /* a bar paused mid-way resumes where it stopped; otherwise it starts again */
        var fill = this.bars[this.current] && this.bars[this.current].firstElementChild;
        if (!fill || fill.getAttribute('data-run') !== 'true') this._run();
      }
    }

    /* ---------- input ---------- */

    _onClick(evt) {
      if (this._swallowClick) {
        /* the click that ends a drag must not follow the slide link */
        this._swallowClick = false;
        evt.preventDefault();
        evt.stopPropagation();
        return;
      }
      var btn = evt.target.closest ? evt.target.closest('.sk-hero__bar, .sk-hero__pause') : null;
      if (!btn || !this.contains(btn)) return;
      if (btn.classList.contains('sk-hero__pause')) this._togglePause();
      else this.goTo(this.bars.indexOf(btn), 0);
    }

    _onKey(evt) {
      if (evt.defaultPrevented || evt.altKey || evt.ctrlKey || evt.metaKey) return;
      if (evt.key === 'ArrowRight') { evt.preventDefault(); this.next(); }
      else if (evt.key === 'ArrowLeft') { evt.preventDefault(); this.prev(); }
    }

    _onDown(evt) {
      if (this.count < 2 || this._drag || evt.isPrimary === false) return;
      if (evt.pointerType === 'mouse' && evt.button !== 0) return;
      if (evt.target.closest && evt.target.closest('.sk-hero__nav')) return;
      this._drag = {
        id: evt.pointerId, x: evt.clientX, y: evt.clientY, on: false, start: 0, dx: 0,
        lx: evt.clientX, lt: evt.timeStamp, v: 0, w: this.getBoundingClientRect().width || 1
      };
      window.addEventListener('pointermove', this._onMove, PASSIVE);
      window.addEventListener('pointerup', this._onUp);
      window.addEventListener('pointercancel', this._onUp);
    }

    _onMove(evt) {
      var d = this._drag;
      if (!d || evt.pointerId !== d.id) return;
      var dx = evt.clientX - d.x;
      var dy = evt.clientY - d.y;
      if (!d.on) {
        if (Math.abs(dy) > HERO_DRAG_START && Math.abs(dy) > Math.abs(dx)) { this._endDrag(); return; } /* a scroll */
        if (Math.abs(dx) < HERO_DRAG_START) return;
        d.on = true;
        this._freeze();
        this._prepareDrag(dx < 0);
        d.start = this.pos;
        this.setAttribute('data-dragging', '');
      }
      var dt = evt.timeStamp - d.lt;
      if (dt > 0) { d.v = (evt.clientX - d.lx) / dt; d.lx = evt.clientX; d.lt = evt.timeStamp; }
      d.dx = dx;
      this._set(Math.max(0, Math.min(this.count - 1, d.start - dx / d.w)), false);
    }

    /* a slide must be ready on the side the drag heads to (two slides: move the other one over) */
    _prepareDrag(forward) {
      var t = this.track;
      var n = this.count;
      if (forward && this.pos > n - 2 && this.pos >= 1) {
        t.appendChild(t.firstElementChild);
        this._set(this.pos - 1, false);
      } else if (!forward && this.pos < 1 && this.pos <= n - 2) {
        t.insertBefore(t.lastElementChild, t.firstElementChild);
        this._set(this.pos + 1, false);
      }
    }

    _onUp(evt) {
      var d = this._drag;
      if (!d || (evt.pointerId !== undefined && evt.pointerId !== d.id)) return;
      this._endDrag();
      if (!d.on) return;
      this.removeAttribute('data-dragging');
      var self = this;
      if (evt.type === 'pointerup') {
        this._swallowClick = true;
        clearTimeout(this._swallowTimer);
        this._swallowTimer = setTimeout(function () { self._swallowClick = false; }, 400);
      }
      /* settle next to where the drag let go: the slide ahead / behind after a swipe or a flick,
         else the nearer one (a short drag goes back); grabbing a move half-way works the same */
      var share = -d.dx / d.w;
      var flick = Math.abs(d.v) > HERO_FLICK_SPEED;
      var dest;
      if (share > HERO_SWIPE_SHARE || (flick && d.v < 0 && d.dx < 0)) dest = Math.ceil(this.pos - 0.001);
      else if (share < -HERO_SWIPE_SHARE || (flick && d.v > 0 && d.dx > 0)) dest = Math.floor(this.pos + 0.001);
      else dest = Math.round(this.pos);
      dest = Math.max(0, Math.min(this.count - 1, dest));
      var target = this.slides.indexOf(this.track.children[dest]);
      /* back on the same slide, its bar goes on from where the drag paused it */
      if (target > -1 && target !== this.current) this._arrive(target);
      this._moveTo(dest);
    }

    _endDrag() {
      window.removeEventListener('pointermove', this._onMove, PASSIVE);
      window.removeEventListener('pointerup', this._onUp);
      window.removeEventListener('pointercancel', this._onUp);
      this._drag = null;
    }

    /* Theme editor: a selected slide stays; selecting the section pauses autoplay. */
    _onEditor(evt) {
      /* section events come from the section around the hero, block events from the slide itself */
      var el = evt.target;
      if (!el || !el.contains || !(el.contains(this) || this.contains(el))) return;
      switch (evt.type) {
        case 'shopify:block:select': {
          var id = evt.detail && evt.detail.blockId;
          var i = this.slides.findIndex(function (s) { return s.getAttribute('data-block-id') === id; });
          this.editorHold = true;
          if (i > -1 && i !== this.current) this.goTo(i, 0);
          else this._run();
          break;
        }
        case 'shopify:section:select':
          this.editorHold = true;
          this._run();
          break;
        default:
          this.editorHold = false;
          this._run();
      }
    }
  }

  if (!customElements.get('sk-hero')) customElements.define('sk-hero', SkHero);
})();
